import React from "react";
import { useTranslation } from "react-i18next";
import { Check } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AVATAR_SECTION_PATH, rememberCheckoutReturn, type CreateAvatarBlock, type FaceLockEligibility } from "@/lib/facelock";
import { api } from "@/lib/api";

// What "Create avatar" opens when the create dialog can't open yet
// (lib/facelock createBlockOf): the button is never a dead greyed button.
//
//   gate    exactly what is missing (sign up on the website, buy N more
//           credits), with a working Buy credits button; the first avatar is
//           free once they are bought (prices and the amount come from the server);
//   pay     signed up, not unlocked: the flat-fee avatar (one checkout), or
//           buy enough credits and the first is free;
//   busy    an avatar is being built right now: the next one after it;
//   closed  the server says no for another reason, in plain words.
//
// z-[400]: it can open from inside the full-screen studio (zIndex 200).

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  block: CreateAvatarBlock | null;
  eligibility: FaceLockEligibility;
  /** On the account page, for a Telegram-studio account: go to its Add your email form. Elsewhere a link to the account page. */
  onAddEmail?: () => void;
  /** Before the flat-fee checkout leaves the page (the storybook keeps its form). */
  onBeforeCheckout?: () => void;
  /** Where the checkout receipt's Continue comes back to (default: this page). */
  returnTo?: { path: string; label: "avatar" | "storybook" };
};

export default function CreateAvatarNeedsDialog({ open, onOpenChange, block, eligibility: e, onAddEmail, onBeforeCheckout, returnTo }: Props) {
  const { t } = useTranslation();
  const [paying, setPaying] = React.useState(false);
  const [payError, setPayError] = React.useState("");
  const flat = e.flat_fee || null;
  const price = flat
    ? t("faceLock.pay.price", { mxn: Math.round(flat.price_cents / 100), usd: flat.usd_cents ? (flat.usd_cents / 100).toFixed(2) : "" })
    : "";
  const pay = async () => {
    if (!flat || paying) return;
    setPaying(true);
    setPayError("");
    try {
      onBeforeCheckout?.();
      const back = returnTo || { path: `${window.location.pathname}${window.location.search}${window.location.hash}`, label: "avatar" as const };
      rememberCheckoutReturn(back.path, back.label);
      const { url } = await api.createCheckout({ pack_type: flat.pack_type });
      window.location.assign(url);
    } catch {
      setPayError(t("faceLock.pay.error"));
      setPaying(false);
    }
  };
  // Add my email runs once the dialog has closed and handed focus back, or
  // the dialog's own focus return would pull focus off the email field.
  const afterClose = React.useRef<(() => void) | null>(null);
  const boughtOk = e.purchased_credits >= e.required_purchased || e.gate_waived === true;
  const missing = Math.max(0, Math.ceil(e.required_purchased - e.purchased_credits));
  const title = block === "busy" ? t("faceLock.needs.busyTitle")
    : block === "closed" ? t("faceLock.needs.closedTitle")
    : block === "pay" ? t("faceLock.pay.title", { price })
    : t("faceLock.gate.title");
  const body = block === "busy" ? t("faceLock.gate.busy")
    : block === "closed" ? t("faceLock.gate.closed")
    : block === "pay" ? t("faceLock.pay.body", { required: e.required_purchased })
    : t("faceLock.gate.body", { required: e.required_purchased });
  const stepClass = (done: boolean) => `flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold ${done ? "bg-emerald-500 text-white" : "bg-muted text-muted-foreground"}`;

  return (
    <Dialog open={open && block !== null} onOpenChange={onOpenChange}>
      <DialogContent
        overlayClassName="z-[400]"
        className="z-[400] max-h-[92vh] overflow-y-auto sm:max-w-md"
        data-testid="facelock-needs-dialog"
        data-block={block || ""}
        onCloseAutoFocus={(ev) => {
          const run = afterClose.current;
          if (!run) return;
          afterClose.current = null;
          ev.preventDefault();
          run();
        }}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription data-testid="facelock-needs-body">{body}</DialogDescription>
        </DialogHeader>

        {block === "gate" && (
          <ol className="grid gap-3 text-sm">
            <li data-testid="facelock-needs-signup" data-done={e.signed_up ? "true" : "false"} className="flex gap-2">
              <span className={stepClass(e.signed_up)}>{e.signed_up ? <Check className="h-3.5 w-3.5" aria-hidden /> : 1}</span>
              <div className="grid gap-1.5">
                <span className="font-medium">{t("faceLock.gate.signUp")}</span>
                {!e.signed_up && (
                  <>
                    <span className="text-muted-foreground">{t("faceLock.gate.signUpTodo")}</span>
                    {onAddEmail ? (
                      <Button type="button" size="sm" variant="outline" className="w-fit" data-testid="facelock-needs-add-email"
                        onClick={() => { afterClose.current = onAddEmail; onOpenChange(false); }}>
                        {t("faceLock.needs.addEmail")}
                      </Button>
                    ) : (
                      <Button asChild size="sm" variant="outline" className="w-fit">
                        <a href={AVATAR_SECTION_PATH} data-testid="facelock-needs-account">{t("faceLock.required.openAccount")}</a>
                      </Button>
                    )}
                  </>
                )}
              </div>
            </li>
            <li data-testid="facelock-needs-credits" data-done={boughtOk ? "true" : "false"} className="flex gap-2">
              <span className={stepClass(boughtOk)}>{boughtOk ? <Check className="h-3.5 w-3.5" aria-hidden /> : 2}</span>
              <div className="grid gap-1.5">
                <span className="font-medium">{t("faceLock.gate.bought", { n: e.purchased_credits, required: e.required_purchased })}</span>
                {!boughtOk && (
                  <>
                    <span className="text-muted-foreground">{t("faceLock.gate.boughtTodo", { count: missing })}</span>
                    <Button asChild size="sm" className="w-fit">
                      <a href="/pricing" data-testid="facelock-needs-buy">{t("faceLock.gate.buyCredits")}</a>
                    </Button>
                  </>
                )}
              </div>
            </li>
          </ol>
        )}

        {block === "pay" && (
          <div className="grid gap-2">
            <Button type="button" size="lg" data-testid="facelock-needs-pay" disabled={paying} onClick={() => { void pay(); }}>
              {paying ? t("faceLock.pay.opening") : t("faceLock.pay.button", { price })}
            </Button>
            <Button asChild variant="outline">
              <a href="/pricing" data-testid="facelock-needs-buy">{t("faceLock.pay.orCredits", { required: e.required_purchased })}</a>
            </Button>
            {payError && <p role="alert" className="text-sm font-medium text-destructive">{payError}</p>}
          </div>
        )}

        <div className="flex justify-end">
          <Button type="button" variant={block === "gate" || block === "pay" ? "ghost" : "default"} data-testid="facelock-needs-ok" onClick={() => onOpenChange(false)}>
            {t("faceLock.needs.ok")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
